import React, { useEffect, useState } from "react";
import { ComponentLoader } from "../common/Loader";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { fetchAllScreens } from "../Configure/store";
import "./DemographicScreen.css";
import { useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const AGE_RANGE_OPTIONS = [
  { value: "1-18", label: "1 - 18" },
  { value: "19-25", label: "19 - 25" },
  { value: "26-35", label: "26 - 35" },
  { value: "36-45", label: "36 - 45" },
  { value: "46-55", label: "46 - 55" },
  { value: "56-65", label: "56 - 65" },
  { value: "65+", label: "65+" },
];

const DemographicScreen = () => {
  const dispatch = useDispatch();
  const [screens, setScreens] = useState([]);
  const abortControllerRef = React.useRef(null);
  const requestTimeoutRef = React.useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Single search term (not used for this specific view currently, but kept for UI consistency)
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Per-column filters
  const [filters, setFilters] = useState({
    userId: "",
    age: "",
    gender: "",
    maritalStatus: "",
    location: "",
    bmi: "",
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [currentPage, setCurrentPage] = useState(1);
  const [goToPage, setGoToPage] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [totalInteractions, setTotalInteractions] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'count', direction: 'desc' });

  const formatNumber = (num) => {
    if (num === "—" || num == null) return "0";
    return Number(num).toLocaleString();
  };


  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Debounce filters
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters]);

  useEffect(() => {
    loadData(currentPage, debouncedFilters);
  }, [
    currentPage,
    debouncedSearch,
    debouncedFilters,
    itemsPerPage,
    sortConfig,
  ]);


  const loadData = (page, specificFilters = filters) => {
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
    }

    requestTimeoutRef.current = setTimeout(async () => {
      const cacheKey = `DEMOGRAPHIC_SCREEN_CACHE_${page}_${itemsPerPage}_${sortConfig.key}_${sortConfig.direction}_${debouncedSearch}_${JSON.stringify(specificFilters)}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      let cacheExists = false;

      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        if (parsed === 'PENDING') {
          return;
        }

        if (parsed && typeof parsed === 'object') {
          const cacheTimestamp = parsed.cacheTimestamp || 0;
          const threeMinutes = 3 * 60 * 1000;
          const isExpired = Date.now() - cacheTimestamp > threeMinutes;

          if (!isExpired) {
            setScreens(parsed.data);
            setTotalCount(parsed.totalCount);
            setTotalInteractions(parsed.totalInteractions || 0);
            setLoading(false);
            return;
          } else {
            setScreens(parsed.data);
            setTotalCount(parsed.totalCount);
            setTotalInteractions(parsed.totalInteractions || 0);
            setLoading(false);
            cacheExists = true;
          }
        }
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      if (!cacheExists) {
        setLoading(true);
      }

      sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));

      dispatch(fetchAllScreens({
        page: Number(page),
        limit: itemsPerPage,
        searchTerm: debouncedSearch,
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...specificFilters,
        signal: abortControllerRef.current.signal
      }))
        .then((response) => {
          if (response.payload && response.payload.success) {
            setScreens(response.payload.data);
            setTotalCount(response.payload.totalCount);
            setTotalInteractions(response.payload.totalInteractions || 0);
            
            sessionStorage.setItem(cacheKey, JSON.stringify({
              data: response.payload.data,
              totalCount: response.payload.totalCount,
              totalInteractions: response.payload.totalInteractions || 0,
              cacheTimestamp: Date.now()
            }));
          } else if (!response.payload?.cancelled) {
            setScreens([]);
            setTotalCount(0);
            setTotalInteractions(0);
            sessionStorage.removeItem(cacheKey);
          }
          setLoading(false);
        })
        .catch((error) => {
          if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
          console.error('Error fetching screens:', error);
          sessionStorage.removeItem(cacheKey);
          Swal.fire("Error", "Failed to load audit data", "error");
          setLoading(false);
        });
    }, 50);
  };

  const handleNext = () => {
    if (currentPage * itemsPerPage < totalCount) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const renderSortHeader = (label, field, className = "") => {
    const isSorted = sortConfig && sortConfig.key === field;
    return (
      <th 
        className={className}
        onClick={() => {
          let direction = 'desc';
          if (isSorted && sortConfig.direction === 'desc') {
            direction = 'asc';
          }
          setSortConfig({ key: field, direction });
          setCurrentPage(1);
        }}
        style={{ cursor: 'pointer', userSelect: 'none', ...(className.includes('center') ? { textAlign: 'center' } : {}) }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: className.includes('center') ? 'center' : 'flex-start' }}>
          {label}
          <div style={{ display: 'flex', flexDirection: 'column', fontSize: '10px' }}>
            <FaChevronUp color={isSorted && sortConfig.direction === 'asc' ? '#000' : '#cbd5e1'} style={{ marginBottom: '-2px' }} />
            <FaChevronDown color={isSorted && sortConfig.direction === 'desc' ? '#000' : '#cbd5e1'} />
          </div>
        </div>
      </th>
    );
  };

  const handleClearData = () => {
    setSearchTerm("");
    setFilters({
      userId: "",
      age: "",
      gender: "",
      maritalStatus: "",
      location: "",
      bmi: ""
    });
  };

  const handleFilterChange = (e, field) => {
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleGoToPage = () => {
    if (!goToPage) return;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const pageNum = parseInt(goToPage, 10);
    if (!pageNum || pageNum < 1 || pageNum > totalPages) {
      Swal.fire('Invalid Page', `Please enter a valid page number (1-${totalPages})`, 'warning');
      return;
    }
    setCurrentPage(pageNum);
    setGoToPage('');
  };

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true);

  return (
    <div className="demo-users-wrapper">
      <div className="demo-modern-header">
        <div className="demo-header-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem' }}>
          <div><h1 className="demo-header-title">Screen Audit Logs</h1>
            <p className="demo-header-subtitle">Analyze screen interactions based on user demographics</p></div>
          <button className="demo-back-btn-modern" onClick={() => navigate("/screen-auditlog")}>← Back</button>
        </div>
      </div>

      <div className="demo-date-range-section">
        <div className="demo-modern-search-container">
          <span className="demo-search-icon-inline">🔍</span>
          <input
            type="text"
            className="demo-modern-search-input"
            placeholder="Search screens..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button className="demo-clear-filters-btn" onClick={handleClearData}>
          <span className="demo-clear-icon">✕</span> Clear Filters
        </button>
      </div>

      <div className="demo-advanced-filters-container">
          <div className="demo-advanced-filters-grid">
            <div className="demo-filter-group">
              <label className="demo-filter-label">USER ID</label>
              <div className="demo-filter-input-wrapper">
                <span className="demo-filter-icon-inline">#</span>
                <input
                  type="text"
                  className="demo-modern-filter-input"
                  placeholder="Ex: 6960..."
                  value={filters.userId}
                  onChange={(e) => handleFilterChange(e, 'userId')}
                />
              </div>
            </div>

            <div className="demo-filter-group">
              <label className="demo-filter-label">AGE</label>
              <div className="demo-filter-input-wrapper">
                <span className="demo-filter-icon-inline">👤</span>
                <select className="demo-modern-filter-select" value={filters.age} onChange={(e) => handleFilterChange(e, 'age')}>
                  <option value="">All Ages</option>
                  {AGE_RANGE_OPTIONS.map((range) => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="demo-filter-group">
              <label className="demo-filter-label">GENDER</label>
              <div className="demo-filter-input-wrapper">
                <span className="demo-filter-icon-inline">⚧</span>
                <select className="demo-modern-filter-select" value={filters.gender} onChange={(e) => handleFilterChange(e, 'gender')}>
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Others">Others</option>
                </select>
              </div>
            </div>

            <div className="demo-filter-group">
              <label className="demo-filter-label">MARITAL STATUS</label>
              <div className="demo-filter-input-wrapper">
                <span className="demo-filter-icon-inline">💍</span>
                <select className="demo-modern-filter-select" value={filters.maritalStatus} onChange={(e) => handleFilterChange(e, 'maritalStatus')}>
                  <option value="">All Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                </select>
              </div>
            </div>

            <div className="demo-filter-group">
              <label className="demo-filter-label">BMI</label>
              <div className="demo-filter-input-wrapper">
                <span className="demo-filter-icon-inline">⚖️</span>
                <select className="demo-modern-filter-select" value={filters.bmi} onChange={(e) => handleFilterChange(e, 'bmi')}>
                  <option value="">All BMI Categories</option>
                  <option value="underweight">Underweight (&lt;18.5)</option>
                  <option value="normal">Normal (18.5 - 24.9)</option>
                  <option value="overweight">Overweight (25 - 29.9)</option>
                  <option value="obese">Obese (&ge;30)</option>
                </select>
              </div>
            </div>

            <div className="demo-filter-group">
              <label className="demo-filter-label">LOCATION</label>
              <div className="demo-filter-input-wrapper">
                <span className="demo-filter-icon-inline">📍</span>
                <input
                  type="text"
                  className="demo-modern-filter-input"
                  placeholder="Filter by city..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange(e, 'location')}
                />
              </div>
            </div>
          </div>
      </div>

      <div className="user-directory-container">
        <div className="directory-header" style={{ display: "flex", justifyContent: "space-between" }}>
          <div><h2 className="directory-title">Audit Log Statistics</h2>
          <p className="directory-subtitle">Total counts of screen views by demographics</p></div>
          <div><span className="demo-total-count-badge">Total Screens: {formatNumber(totalCount)}</span></div>
        </div>

        <div className="table-scroll-wrapper" style={{ maxHeight: '450px', overflowY: 'auto' }}>
          <table className="modern-table" style={{ position: 'relative' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fdfbfb', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
              <tr className="modern-table-header">
                <th className="th-index" style={{ width: '80px' }}>S.NO</th>
                {renderSortHeader("SCREEN NAME", "screenName", "th-user-info")}
                {renderSortHeader("COUNTS", "count", "th-count center")}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" style={{ padding: '80px 0' }}>
                    <ComponentLoader color="#4f46e5" size="40px" />
                  </td>
                </tr>
              ) : screens.length > 0 ? (
                screens.map((screen, index) => (
                  <tr key={index} className="modern-table-row">
                    <td className="td-index">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="td-user-info">
                      <div className="user-info-cell">
                        <div className="user-id" style={{ fontSize: '15px', fontWeight: '600' }}>
                          {screen.screenName || "Unknown Screen"}
                        </div>
                      </div>
                    </td>
                    <td className="td-count" style={{ textAlign: 'center' }}>
                      <span className="count-badge">
                        {formatNumber(screen.count)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="no-data-cell">
                    <div className="no-data-message">
                      <span className="no-data-icon">📭</span>
                      <p>No audit data found for selected filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && totalCount > 0 && (
          <div className="pq-pagination-container d-flex justify-content-between align-items-center" style={{ padding: '16px', borderTop: '1px solid #e2e8f0', marginTop: '1rem' }}>
              <div className="d-flex align-items-center">
                  <span className="pq-pagination-text me-2">Showing {totalCount > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} – {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries. Per page: </span>
                  <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="pq-filter-input" style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important' }}>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                  </select>
              </div>

              <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item pq-page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link pq-page-link" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                          &laquo;
                      </button>
                  </li>

                  {[...Array(Math.ceil(totalCount / itemsPerPage))].map((_, i) => {
                      const totalPages = Math.ceil(totalCount / itemsPerPage);
                      if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                          return (
                              <li className={`page-item pq-page-item ${i + 1 === currentPage ? 'active' : ''}`} key={i}>
                                  <button className="page-link pq-page-link" onClick={() => setCurrentPage(i + 1)}>
                                      {i + 1}
                                  </button>
                              </li>
                          );
                      }
                      if (i === 1 && currentPage > 3) return <li className="page-item pq-page-item disabled" key="start-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                      if (i === totalPages - 2 && currentPage < totalPages - 2) return <li className="page-item pq-page-item disabled" key="end-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                      return null;
                  })}

                  <li className={`page-item pq-page-item ${currentPage === Math.ceil(totalCount / itemsPerPage) || totalCount === 0 ? 'disabled' : ''}`}>
                      <button className="page-link pq-page-link" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === Math.ceil(totalCount / itemsPerPage) || totalCount === 0}>
                          &raquo;
                      </button>
                  </li>
              </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemographicScreen;
